import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import UnitImage from "common/src/tft-static/UnitImage";
// import { make as UnitImageExtended } from "common/src/tft-static/UnitImageExtended.bs";
import ItemImage from "common/src/tft-static/ItemImage";
import AugmentImage from "common/src/tft-static/AugmentImage";
import { PlainIcon } from "common/src/tft-static/TraitImage";
import { css } from "@emotion/css";
import React from "react";
import type { Filter } from "./ExState";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { ListboxComponent } from "../components/BaseAutocomplete";

type Props = {
  options: Array<Filter>;
  onChange: (v: Filter | undefined) => void;
  value?: Filter;
  placeholder?: string;
  getMatches: (search: string) => Array<Filter>;
  disabled?: boolean;
  allowItemFilters: boolean;
};

const ExAutocomplete = ({
  options,
  onChange,
  value,
  placeholder = undefined,
  getMatches,
  disabled,
  allowItemFilters,
}: Props) => {
  const startAdornment = React.useCallback((v: Filter) => {
    if (v.typ == "u") {
      return <UnitImage unitId={v.id} size={28} />;
    } else if (v.typ == "t") {
      return <PlainIcon traitId={v.id} size={28} rank={v.rank} />;
    } else if (v.typ == "i") {
      return <ItemImage itemId={v.id} size={28} rounded="rounded" />;
    } else if (v.typ == "a") {
      return <AugmentImage augId={v.id} size={28} />;
    }
  }, []);

  const renderOption = React.useCallback((props: any, v: Filter) => {
    let el = null;
    if (v.typ == "u") {
      el = <UnitImage unitId={v.id} size={28} />;
    } else if (v.typ == "t") {
      el = <PlainIcon traitId={v.id} size={28} rank={v.rank} />;
    } else if (v.typ == "i") {
      // <UnitImage unitId={v.unitId} size={28} />
      if (v.id === "0") {
        el = (
          <div className="flex items-center text-sm h-12 leading-tight">
            <div className="pr-2">
              <InfoIcon />
            </div>
            Item filters require a unit filter selected first
          </div>
        );
      } else {
        el = <ItemImage itemId={v.id} size={28} rounded="rounded" />;
      }
    } else if (v.typ == "a") {
      el = <AugmentImage augId={v.id} size={28} />;
    }

    return (
      <div key={v.label} {...props}>
        {el}
        <span className="font-montserrat leading-tight font-medium inline text-base pl-[6px]">
          {v.label}
        </span>
      </div>
    );
  }, []);

  return (
    <Autocomplete
      disabled={disabled}
      sx={{ width: 225 }}
      classes={{
        popper: css({ minWidth: "275px" }),
      }}
      value={value}
      onChange={(_, v: Filter | null) => onChange(v || undefined)}
      filterOptions={(vs, state) => {
        if (state.inputValue) {
          const filters = getMatches(state.inputValue);
          if (allowItemFilters) {
            return filters;
          } else {
            const itemFilters = [];
            const nonItemFilters: Array<Filter> = [];
            filters.forEach((f) => {
              if (f.typ === "i") {
                itemFilters.push(f);
              } else {
                nonItemFilters.push(f);
              }
            });
            if (itemFilters.length !== 0) {
              nonItemFilters.push({
                typ: "i",
                id: "0",
                unitId: ["", 0],
                label: "",
              });
            }
            return nonItemFilters;
          }
        } else {
          if (allowItemFilters) {
            return vs;
          } else {
            return vs.filter((f) => f.typ !== "i");
          }
        }
      }}
      // @ts-ignore
      ListboxComponent={ListboxComponent}
      options={options}
      renderOption={renderOption}
      autoHighlight
      renderInput={(params) => {
        return (
          <TextField
            placeholder={placeholder}
            {...params}
            inputProps={{
              ...params.inputProps,
              // @ts-ignore
              value: params.inputProps.value
                ? // @ts-ignore
                  params.inputProps.value
                : value?.label || "",
            }}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              classes: {
                input: "font-montserrat font-medium",
              },
              startAdornment: (
                <InputAdornment position="start">
                  {value && startAdornment(value)}
                </InputAdornment>
              ),
            }}
          />
        );
      }}
    />
  );
};

export default ExAutocomplete;
